import { PageContainer } from '@components/PageContainer';
import { useBankProfile } from '@contexts/BankProfileContext';
import { Loader, Typography } from '@gurupay/gp-ui-library';
import { useMarkMessageAsRead, useMessageThread } from '@hooks/useMessage';
import { useUnreadThreadsCount } from '@hooks/useUnreadThreadsCount';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';

import { ThreadMessageList } from './ThreadMessageList';
import { ThreadReplyForm } from './ThreadReplyForm';

const Threads = () => {
	const { t } = useTranslation('', { keyPrefix: 'messages' });
	const { id: threadId } = useParams();
	const {
		data: threadData,
		isLoading,
		isFetching,
		isError,
	} = useMessageThread(threadId!);
	const { mutateAsync: markAsRead } = useMarkMessageAsRead();
	const { selectedProfile } = useBankProfile();
	const { refetch: refetchUnreadThreadCount } = useUnreadThreadsCount();

	const markAllAsRead = async () => {
		if (!threadData?.messages) {
			return;
		}

		const unreadMessages = threadData.messages.filter((msg) => msg.read_at === null);

		if (!unreadMessages.length) {
			return;
		}

		await Promise.all(
			unreadMessages.map((msg) =>
				markAsRead({ threadId: threadId!, messageId: msg.uuid }),
			),
		);
		await refetchUnreadThreadCount();
	};

	useEffect(() => {
		if (!isLoading) markAllAsRead();
	}, [isLoading]);

	const threadAuthor =
		!isLoading && threadData?.customer_id === selectedProfile?.id
			? t('user')
			: 'Guru Pay';

	if (isError) {
		return <Navigate replace to={'messages'} />;
	}

	const topicValue = threadData?.topic?.value;
	const topicTitle = topicValue
		? `${t('subject')} ${t(`topic.${topicValue}`, { defaultValue: t('topic.other-topic') })}`
		: t('title');

	return (
		<PageContainer backUrl={'/messages'} title={topicTitle}>
			{isLoading || !selectedProfile ? (
				<div className="flex justify-center items-center h-125">
					<Loader />
				</div>
			) : (
				<>
					<div className="flex justify-end">
						<Typography level="small-text">
							{t('from')} <span className="font-bold">{threadAuthor}</span>
						</Typography>
					</div>
					<div className="border-t border-t-neutral-100 pt-space-40 h-screen flex flex-col">
						<ThreadMessageList messages={threadData?.messages} />
						<ThreadReplyForm
							isClosed={threadData?.is_closed}
							isFetching={isFetching}
							threadId={threadId!}
							topic={threadData?.topic.value}
						/>
					</div>
				</>
			)}
		</PageContainer>
	);
};

export default Threads;
